import React, { useEffect, useRef, useState } from "react";
import {
  Card,
  CardBody,
  Button,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import "./dashboard.scss";
import ReactApexChart from "react-apexcharts";
import { getRecords } from "../../services/api.service";
import { useNavigate } from "react-router-dom";

const Installation = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const years = Array.from(
    { length: currentYear - 2000 + 1 },
    (_, i) => 2000 + i
  );
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [series, setSeries] = useState([
    { name: "installed", type: "column", data: Array(12).fill(0) },
    { name: "claimed", type: "column", data: Array(12).fill(0) },
    { name: "paid", type: "column", data: Array(12).fill(0) },
  ]);
  const [loading, setLoading] = useState(true);
  const [isFetch, setIsFetch] = useState(false);
  const [typeCostQTY, setTypeCostQTY] = useState(null);
  const [formOptions, setFormOptions] = useState([
    { id: null, formName: "All Forms" },
  ]);
  const [selectedFormId, setSelectedFormId] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const hasUserSelectedForm = useRef(false);

  const toggleDropdown = () => setDropdownOpen((prevState) => !prevState);

  // Skeleton styles
  const skeletonStyle = {
    backgroundColor: "#f0f0f0",
    borderRadius: "4px",
    position: "relative",
    overflow: "hidden",
    background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
    backgroundSize: "200% 100%",
    animation: "shimmer 1.5s infinite linear",
  };

  useEffect(() => {
    let timer;

    const fetchData = async () => {
      const dashboard_details = localStorage.getItem("dashboard_details");
      if (dashboard_details && !isFetch) {
        await fetchForms();
        setIsFetch(true);
        setLoading(false);
      }
    };

    fetchData();

    const handleStorageChange = () => {
      hasUserSelectedForm.current = false;
      fetchForms();
      fetchReports();
      setIsFetch(true);
    };

    window.addEventListener("dashboardUpdate", handleStorageChange);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("dashboardUpdate", handleStorageChange);
    };
  }, [selectedYear, selectedFormId, isFetch]);

  useEffect(() => {
    if (selectedFormId.length > 0 && selectedYear && !isFetch) {
      fetchReports();
    }
  }, [selectedFormId, selectedYear]);

  useEffect(() => {
  if (selectedYear && !isFetch) {
    if (selectedFormId.length === 0) {
      setSeries([
        { name: "installed", type: "column", data: Array(12).fill(null) },
        { name: "claimed", type: "column", data: Array(12).fill(null) },
        { name: "paid", type: "column", data: Array(12).fill(null) },
      ]);
      setLoading(false);
    } else {
      fetchReports();
    }
  }
}, [selectedFormId, selectedYear]);


  const handleCheckboxChange = (formId) => {
    hasUserSelectedForm.current = true;

    const nonNullIds = formOptions
      .filter((f) => f.id !== null)
      .map((f) => f.id);

    if (formId === null) {
      if (
        selectedFormId.length === nonNullIds.length &&
        nonNullIds.every((id) => selectedFormId.includes(id))
      ) {
        setSelectedFormId([]);
      } else {
        setSelectedFormId(nonNullIds);
      }
    } else {
      let updated = selectedFormId.includes(formId)
        ? selectedFormId.filter((id) => id !== formId)
        : [...selectedFormId, formId];

      const allSelected =
        updated.length === nonNullIds.length &&
        nonNullIds.every((id) => updated.includes(id));

      setSelectedFormId(updated);
    }

    setIsFetch(false);
  };

  const fetchForms = async () => {
    try {
      const dashboard_details = localStorage.getItem("dashboard_details");
      if (!dashboard_details) return;

      const parsedDetails = JSON.parse(dashboard_details);
      const payload = {
        ...parsedDetails,
        year: selectedYear,
      };

      const response = await getRecords(
        payload,
        "/dashboard/getMonthWiseInstallationCount"
      );

      if (response?.code === 1 && response.data?.forms) {
        const allForms = response.data.forms;
        setFormOptions([{ id: null, formName: "All Forms" }, ...allForms]);
        if (!hasUserSelectedForm.current) {
          const nonNullIds = allForms.map((form) => form.id);
          setSelectedFormId(nonNullIds);
          await fetchReports(nonNullIds);
        }
      }
    } catch (error) {
      console.error("Error fetching forms:", error);
    }
  };

  const fetchReports = async (formIds = selectedFormId) => {
    try {
      const dashboard_details = localStorage.getItem("dashboard_details");
      if (!dashboard_details) return;

      const payload = {
        ...JSON.parse(dashboard_details),
        year: selectedYear,
        form_id:
          formIds.length === 1 ? formIds[0] : formIds,
      };
      setTypeCostQTY(payload.type);

      const response = await getRecords(
        payload,
        "/dashboard/getMonthWiseInstallationCount"
      );

      if (response?.code === 1 && response.data?.count) {
        const sanitizedData = response.data.count.map((series) => ({
          ...series,
          data: series?.data.map((value) => {
            const numericValue =
              typeof value === "string" ? parseFloat(value) : value;
            return numericValue === 0 ? null : numericValue;
          }),
        }));
        setSeries(sanitizedData);
      } else {
        console.error("Unexpected API response format", response);
      }
    } catch (error) {
      console.error("Error fetching reports:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleYearChange = (event) => {
    setSelectedYear(parseInt(event.target.value, 10));
    setIsFetch(false);
  };

  const options = {
    chart: { toolbar: { show: false } },
    stroke: { width: [0, 3], curve: "smooth" },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "25%",
        dataLabels: { position: "top" },
      },
    },
    dataLabels: { enabled: false },
    legend: {
      show: true,
      markers: {
        shape: "square",
        offsetX: -3,
        offsetY: -2,
      },
      itemMargin: { horizontal: 10, vertical: 0 },
    },
    colors: ["#ff751a", "#4CAF50", "#2196F3"],
    labels: [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ],
    xaxis: { tooltip: { enabled: false } },
    yaxis: {
      title: { text: typeCostQTY === "cost" ? "Cost" : "Quantity" },
      labels: {
        formatter: function (value) {
          if (value === null || value === undefined) return "";

          if (value >= 10000000) {
            return (value / 10000000).toFixed(1) + "Cr";
          } else if (value >= 100000) {
            return (value / 100000).toFixed(1) + "L";
          } else if (value >= 1000) {
            return (value / 1000).toFixed(1) + "K";
          } else {
            return value.toString();
          }
        },
      },
    },
    tooltip: {
      y: {
        formatter: function (value) {
          return value?.toLocaleString("en-IN");
        },
      },
    },
  };

  return (
    <Card>
      <CardBody>
        <style>
          {`
            @keyframes shimmer {
              to { background-position: -200% 0; }
            }
          `}
        </style>

        <div className="float-end d-flex gap-3">
          <div>
            {loading ? (
              <div
                style={{ ...skeletonStyle, width: "120px", height: "40px" }}
              />
            ) : (
              <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
                <DropdownToggle
                  caret
                  className="form-select-sm"
                  style={{
                    backgroundColor: "white",
                    border: "1px solid #D2D2D2",
                    color: "#495057",
                  }}
                >
                  {selectedFormId.length === formOptions.length - 1
                    ? "All Forms"
                    : selectedFormId.length === 0
                    ? "Select Forms"
                    : `${selectedFormId.length} selected`}
                </DropdownToggle>
                <DropdownMenu style={{ maxHeight: "300px", overflowY: "auto" }}>
                  {formOptions.map((form) => (
                    <DropdownItem key={form.id || "null"} toggle={false}>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id={`form-${form.id}`}
                          checked={
                            form.id === null
                              ? selectedFormId.length ===
                                formOptions.filter((f) => f.id !== null).length
                              : selectedFormId.includes(form.id)
                          }
                          onChange={() => handleCheckboxChange(form.id)}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`form-${form.id}`}
                        >
                          {form.formName}
                        </label>
                      </div>
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
            )}
          </div>
          {loading ? (
            <div
              style={{
                ...skeletonStyle,
                width: "100px",
                height: "30px",
              }}
            />
          ) : (
            <div>
              <select
                className="form-select form-select-sm"
                style={{ minHeight: "40px", overflowY: "auto" }}
                value={selectedYear}
                onChange={handleYearChange}
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <h4 className="card-title mb-2">
          {loading ? (
            <div
              style={{
                ...skeletonStyle,
                width: "150px",
                height: "24px",
              }}
            />
          ) : (
            "Installation"
          )}
        </h4>

        <div id="line-column-chart" className="apex-charts" dir="ltr">
          {loading ? (
            <div
              style={{
                ...skeletonStyle,
                height: "300px",
                width: "100%",
              }}
            />
          ) : (
            <ReactApexChart
              options={options}
              series={series}
              type="line"
              height={300}
            />
          )}
        </div>

        <div className="d-flex justify-content-end mt-2">
          {loading ? (
            <div
              style={{
                ...skeletonStyle,
                width: "100px",
                height: "24px",
              }}
            />
          ) : (
            <Button
              color="link"
              className="d-flex align-items-center justify-content-center fw-bold p-0 text-decoration-none"
              onClick={() =>
                navigate("/installation-report", {
                  state: { year: selectedYear, formId: selectedFormId },
                })
              }
            >
              Details <i className="ri-arrow-right-line fs-5 ms-1"></i>
            </Button>
          )}
        </div>
      </CardBody>
    </Card>
  );
};

export default Installation;
